import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Skip auth check if we're about to process a Google OAuth callback.
    // AuthCallback will set the token first and then call refresh manually.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: formatApiError(err) };
    }
  };

  const register = async (name, email, password, referrerCode, consent = {}) => {
    try {
      const { data } = await api.post("/auth/register", {
        name,
        email,
        password,
        referrer_code: referrerCode,
        age_confirmed: !!consent.age_confirmed,
        terms_accepted: !!consent.terms_accepted,
        privacy_accepted: !!consent.privacy_accepted,
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { success: true, user: data.user, referred: !!data.referred };
    } catch (err) {
      return { success: false, error: formatApiError(err) };
    }
  };

  const sendPhoneOtp = async (phone, purpose = "signup") => {
    try {
      const { data } = await api.post("/auth/otp/send", { phone, purpose });
      return { success: true, expires_in: data.expires_in, debug_otp: data.debug_otp };
    } catch (err) {
      return { success: false, error: formatApiError(err) };
    }
  };

  const phoneSignup = async ({ phone, otp, name, password, email, referrerCode, consent = {} }) => {
    try {
      const payload = {
        phone, otp, name, password,
        age_confirmed: !!consent.age_confirmed,
        terms_accepted: !!consent.terms_accepted,
        privacy_accepted: !!consent.privacy_accepted,
      };
      if (email) payload.email = email;
      if (referrerCode) payload.referrer_code = referrerCode;
      const { data } = await api.post("/auth/otp/verify-signup", payload);
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { success: true, user: data.user, referred: !!data.referred };
    } catch (err) {
      return { success: false, error: formatApiError(err) };
    }
  };

  const confirmAge = async () => {
    try {
      const { data } = await api.post("/auth/confirm-age", {
        age_confirmed: true, terms_accepted: true, privacy_accepted: true,
      });
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: formatApiError(err) };
    }
  };

  const phoneLogin = async ({ phone, otp }) => {
    try {
      const { data } = await api.post("/auth/otp/verify-login", { phone, otp });
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: formatApiError(err) };
    }
  };

  const googleLogin = async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId });
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: formatApiError(err) };
    }
  };

  const logout = async () => {
    // Revoke on the server + broadcast to every open tab.
    try {
      const { performLogout } = await import("@/lib/authRefresh");
      await performLogout();
    } catch (_) { /* fall through — client wipe still happens */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const updateProfile = async (patch) => {
    try {
      const { data } = await api.patch("/auth/me", patch);
      setUser(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: formatApiError(err) };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleLogin, sendPhoneOtp, phoneSignup, phoneLogin, logout, refresh, updateProfile, confirmAge }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
